import { FormEvent, useCallback, useEffect, useState } from "react";
import {
  Globe,
  Plus,
  Trash2,
  Check,
  Copy,
  Star,
  ShieldCheck,
  AlertCircle,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import { useFeature, UpgradeBadge } from "../../../marketplace/FeatureGate";
import { Domain } from "../../../types";
import HeroSection from "../../../components/ui/HeroSection";
import Button from "../../../components/ui/Button";
import EmptyState from "../../../components/ui/EmptyState";
import Skeleton from "../../../components/ui/Skeleton";
import Badge from "../../../components/ui/Badge";
import HelpHint from "../../../components/ui/HelpHint";
import { useConfirm } from "../../../components/ui/ConfirmDialog";
import { copyToClipboard, formatDate } from "../../../utils";
import env from "../../../config";

const Domains = ({ embedded = false }: { embedded?: boolean }) => {
  const services = useAppServices();
  const confirm = useConfirm();
  const { openUpgrade, refreshCustomDomain } = useMarketplace();
  const feat = useFeature("customDomain");

  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [host, setHost] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [verifyMsg, setVerifyMsg] = useState<Record<string, string>>({});
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [defaultingId, setDefaultingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchDomains = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const { response } = await services.url_domains.get();
    if (Array.isArray(response)) setDomains(response);
    if (!silent) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (feat.allowed) fetchDomains();
    else setLoading(false);
  }, [feat.allowed, fetchDomains]);

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const value = host.trim().toLowerCase().replace(/^https?:\/\//, "");
    if (!value || !/^[a-z0-9.-]+\.[a-z]{2,}$/.test(value)) {
      setError("Enter a valid hostname like go.acme.com");
      return;
    }
    setSubmitting(true);
    const { response, error: err } = await services.url_domains.create({
      payload: { host: value },
    });
    setSubmitting(false);
    if (err) {
      setError(err.error || err.message || "Failed to add domain");
      return;
    }
    if (response) setDomains((curr) => [response, ...curr]);
    setHost("");
  };

  const handleVerify = async (d: Domain) => {
    setVerifyMsg((m) => ({ ...m, [d._id]: "" }));
    setVerifyingId(d._id);
    try {
      const { response, error: err } = await services.url_domains.verify({
        payload: { _id: d._id },
      });
      if (err) {
        setVerifyMsg((m) => ({
          ...m,
          [d._id]: err.message || "Couldn't verify the domain yet.",
        }));
        return;
      }
      if (response) {
        // The verify response is { host, type, target } — not the full domain —
        // so flip `verified` locally rather than replacing the row, then re-sync
        // from the server to pick up any other server-side changes.
        setDomains((curr) =>
          curr.map((x) => (x._id === d._id ? { ...x, verified: true } : x)),
        );
        refreshCustomDomain();
        fetchDomains(true);
      }
    } finally {
      setVerifyingId(null);
    }
  };

  const handleSetDefault = async (d: Domain) => {
    setDefaultingId(d._id);
    try {
      const { response } = await services.url_domains.setDefault({
        payload: { _id: d._id },
      });
      if (response) {
        setDomains((curr) =>
          curr.map((x) => ({ ...x, is_default: x._id === response._id })),
        );
        refreshCustomDomain();
      }
    } finally {
      setDefaultingId(null);
    }
  };

  const handleDelete = async (d: Domain) => {
    const ok = await confirm({
      title: `Remove ${d.host}?`,
      message: "Links using this branded domain will fall back to the default host.",
      confirmLabel: "Remove domain",
    });
    if (!ok) return;
    setDeletingId(d._id);
    try {
      await services.url_domains.delete({
        query: `_id=${d._id}`,
      });
      setDomains((curr) => curr.filter((x) => x._id !== d._id));
      refreshCustomDomain();
    } finally {
      setDeletingId(null);
    }
  };

  const copyTarget = async (d: Domain) => {
    const ok = await copyToClipboard(env.APP_HOST);
    if (ok) {
      setCopiedId(d._id);
      setTimeout(() => setCopiedId(null), 1500);
    }
  };

  // Feature-gated view for Free / Pro users.
  if (!feat.allowed) {
    return (
      <div className="space-y-6">
        {!embedded && (
          <HeroSection
            eyebrow={
              <>
                <Globe size={11} className="text-brand-500" />
                <span>Domains</span>
              </>
            }
            title="Brand your short links"
            description="Use your own domain like go.acme.com instead of the default. Track every click with a URL that looks like yours."
          />
        )}
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
          <EmptyState
            icon={<Globe size={26} />}
            tone="amber"
            title="Custom domains are a Pro feature"
            description="With a branded domain, every shared link reinforces your brand. Available on the Pro plan."
            action={
              <div className="flex items-center gap-2">
                <UpgradeBadge feature="customDomain" />
                <Button
                  variant="gradient"
                  onClick={openUpgrade}
                  iconRight={<ArrowRight size={14} />}
                >
                  Upgrade to Pro
                </Button>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!embedded && (
        <HeroSection
          eyebrow={
            <>
              <Globe size={11} className="text-brand-500" />
              <span>Domains</span>
            </>
          }
          title="Your branded domains"
          description="Add a custom domain like go.acme.com. After DNS verification, all your short links will use it."
        />
      )}

      {/* Add domain form */}
      <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card p-5">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-ink-900 tracking-tight mb-3">
          Add a domain
          <HelpHint title="Branded domain" anchor="field-domain">
            Enter a subdomain you own, like <strong>go.yourbrand.com</strong>.
            You'll add a DNS record to verify it, after which your short links
            can be built on it instead of the default host.
          </HelpHint>
        </h3>
        <form onSubmit={handleAdd} className="flex flex-wrap items-start gap-2">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <Globe
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
              />
              <input
                type="text"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="go.acme.com"
                className="w-full pl-9 pr-3 h-10 bg-white border border-ink-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
              />
            </div>
            {error && (
              <div className="mt-1.5 text-xs text-rose-700">{error}</div>
            )}
          </div>
          <Button
            type="submit"
            variant="gradient"
            size="lg"
            loading={submitting}
            icon={!submitting ? <Plus size={14} strokeWidth={2.5} /> : undefined}
          >
            Add domain
          </Button>
        </form>
      </div>

      {/* Domain list */}
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-24 w-full rounded-2xl" />
        </div>
      ) : domains.length === 0 ? (
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
          <EmptyState
            icon={<Globe size={26} />}
            tone="brand"
            title="No domains yet"
            description="Add your first branded domain above. After DNS verification, your short links will use it instead of the default URL."
          />
        </div>
      ) : (
        <div className="space-y-3">
          {domains.map((d) => (
            <DomainCard
              key={d._id}
              domain={d}
              appHost={env.APP_HOST}
              copied={copiedId === d._id}
              verifyMsg={verifyMsg[d._id]}
              verifying={verifyingId === d._id}
              defaulting={defaultingId === d._id}
              deleting={deletingId === d._id}
              onVerify={handleVerify}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              onCopyTarget={copyTarget}
            />
          ))}
        </div>
      )}

      <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card p-5 text-xs text-ink-500">
        <p className="font-medium text-ink-700 mb-1">How it works</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>
            Add a subdomain you own above, e.g.{" "}
            <code className="font-mono text-ink-700 bg-ink-100 px-1 rounded">
              go.acme.com
            </code>
            .
          </li>
          <li>
            In your DNS provider, create a <strong>CNAME</strong> record from
            that subdomain to{" "}
            <code className="font-mono text-ink-700 bg-ink-100 px-1 rounded">
              {env.APP_HOST}
            </code>
            .
          </li>
          <li>
            Click <strong>Verify</strong>. We check the DNS is pointing to us,
            then issue an SSL certificate automatically.
          </li>
          <li>
            Set the domain as <strong>default</strong> and your short links are
            served from it over HTTPS.
          </li>
        </ol>
      </div>
    </div>
  );
};

const DomainCard = ({
  domain,
  appHost,
  copied,
  verifyMsg,
  verifying,
  defaulting,
  deleting,
  onVerify,
  onSetDefault,
  onDelete,
  onCopyTarget,
}: {
  domain: Domain;
  appHost: string;
  copied: boolean;
  verifyMsg?: string;
  verifying: boolean;
  defaulting: boolean;
  deleting: boolean;
  onVerify: (d: Domain) => void;
  onSetDefault: (d: Domain) => void;
  onDelete: (d: Domain) => void;
  onCopyTarget: (d: Domain) => void;
}) => {
  return (
    <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-50 to-violet-50 text-brand-600 flex items-center justify-center shrink-0">
              <Globe size={16} />
            </div>
            <div className="font-display font-semibold text-ink-900 text-lg truncate">
              {domain.host}
            </div>
            {domain.verified ? (
              <Badge tone="emerald" icon={<ShieldCheck size={10} />}>
                Verified
              </Badge>
            ) : (
              <Badge tone="amber" icon={<AlertCircle size={10} />}>
                Pending DNS
              </Badge>
            )}
            {domain.is_default && (
              <Badge tone="brand" icon={<Star size={10} />}>
                Default
              </Badge>
            )}
          </div>
          <div className="text-xs text-ink-500 mt-1 ml-11">
            Added {formatDate(domain.createdAt)}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!domain.verified && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onVerify(domain)}
              loading={verifying}
              icon={!verifying ? <ShieldCheck size={13} /> : undefined}
            >
              {verifying ? "Verifying…" : "Verify"}
            </Button>
          )}
          {domain.verified && !domain.is_default && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onSetDefault(domain)}
              loading={defaulting}
              icon={!defaulting ? <Star size={13} /> : undefined}
            >
              Set as default
            </Button>
          )}
          <button
            onClick={() => onDelete(domain)}
            disabled={deleting}
            title="Remove"
            className="w-8 h-8 rounded-lg hover:bg-rose-50 text-ink-500 hover:text-rose-600 flex items-center justify-center transition-all disabled:opacity-50"
          >
            {deleting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Trash2 size={14} />
            )}
          </button>
        </div>
      </div>

      {!domain.verified && (
        <div className="mt-4 ml-11 pt-3 border-t border-ink-100">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-2">
            DNS setup
          </div>
          <div className="text-xs text-ink-600 mb-2">
            Create a <strong>CNAME</strong> record on{" "}
            <span className="font-mono">{domain.host}</span> pointing to:
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 h-9 inline-flex items-center rounded-lg bg-ink-50 border border-ink-200 text-ink-700 font-mono text-xs break-all">
              {appHost}
            </code>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onCopyTarget(domain)}
              icon={
                copied ? (
                  <Check size={13} className="text-emerald-600" />
                ) : (
                  <Copy size={13} />
                )
              }
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          {verifyMsg && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-rose-700">
              <AlertCircle size={13} className="shrink-0 mt-0.5" />
              <span>{verifyMsg}</span>
            </div>
          )}
          <p className="text-[11px] text-ink-400 mt-2">
            After adding the record, click Verify. DNS changes can take a few
            minutes to propagate.
          </p>
        </div>
      )}
    </div>
  );
};

export default Domains;
